import { Box, Stack, Typography } from "@mui/material";
import { CONDENSED_FONT_FAMILY } from "../constants";
import type {
  PlayerStatSection,
  PlayerStatsPlayerResponse,
  PlayerStatsScopeRow,
} from "../../models/PlayerStats";
import { selectSections, TemplateId } from "./sectionRules";
import {
  ActionProfile,
  AdvancedSnapshot,
  BoxScoreTable,
  BreakdownTable,
  ObsShotsTable,
  CloseoutsSection,
  HeaderBand,
  ImpactHistory,
  Kicker,
  MatchupRoles,
  SeasonHistory,
  WhoHeGuards,
} from "./sections";
import ShotChartSection from "./ShotChartSection";

/**
 * Report-native player page rendered from the standalone roll-up blob
 * (team-only report architecture), laid out on the personnel report's grid:
 * numbered full-width baseline sections, two-column pairings, KPI bands.
 * Section selection is rule-based (sectionRules.ts) with the per-player
 * override hook.
 */

// Which served drill-down sections each feature template renders, in order.
// The old unified-scheme ".coverage" breakdowns stay excluded; ".cov" is the
// combined NEW-language presentation (up/drop/switch/blitz scheme + separate
// under/push BH navigation, composed server-side from both blob families).
// Ball-screen templates list both key sets: NBA blobs split PR / Sm-Sm (one
// grid row per class), SkillCorner blobs keep the all-screens sections. A
// section the scope has no data for renders nothing.
const FEATURE_SECTIONS: Partial<Record<TemplateId, string[]>> = {
  pnrBallhandler: [
    "offense.pr_bhr.cov",
    "offense.pr_bhr.bhr_outcome",
    "offense.smsm_bhr.cov",
    "offense.smsm_bhr.bhr_outcome",
    "offense.pick_bhr.cov",
    "offense.pick_bhr.size",
    "offense.pick_bhr.bhr_outcome",
  ],
  pnrScreener: [
    "offense.pr_screener.roll_type",
    "offense.smsm_screener.roll_type",
    "offense.pnr_screener.roll_type",
    "offense.pnr_screener.size",
  ],
  isolation: ["offense.isolation."],
  drives: ["offense.drive."],
  offBallScreens: ["offense.obs."],
  screenDefense: ["defense.pr_screener.", "defense.smsm_screener.", "defense.pnr_screener."],
};

function resolveFeatureSections(
  template: TemplateId,
  sections: PlayerStatSection[],
): PlayerStatSection[] {
  const wants = FEATURE_SECTIONS[template];
  if (!wants) return [];
  const out: PlayerStatSection[] = [];
  for (const want of wants) {
    if (want.endsWith(".")) {
      out.push(
        ...sections
          .filter((s) => s.key.startsWith(want) && !s.key.endsWith(".coverage"))
          .slice(0, 2),
      );
    } else {
      const s = sections.find((x) => x.key === want);
      if (s) out.push(s);
    }
  }
  return out;
}

/** Two-column pairing grid (personnel page layout). Paper width sits under the
 *  `md` breakpoint, so without the print override the pairs collapse to one
 *  column in print/PDF. */
function Pair({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        columnGap: 5,
        alignItems: "start",
        "@media print": { gridTemplateColumns: "1fr 1fr" },
      }}
    >
      {children}
    </Box>
  );
}

export default function PlayerBlobPage({
  playerId,
  playerName,
  blob,
  season,
}: {
  playerId: number;
  playerName: string;
  blob?: PlayerStatsPlayerResponse;
  season?: string;
}) {
  if (!blob?.player) {
    return (
      <Stack spacing={1} sx={{ px: 2, fontFamily: CONDENSED_FONT_FAMILY }}>
        <HeaderBand name={playerName} />
        <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 1, p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No roll-up blob found for {playerName} (id {playerId}) — the page will
            render once the blob exists.
          </Typography>
        </Box>
      </Stack>
    );
  }

  const { player, scopes, sections } = blob;
  const seasonNum = season ? Number(season) : undefined;
  const scope: PlayerStatsScopeRow | undefined =
    scopes.find((s) => seasonNum !== undefined && s.season === seasonNum) ??
    scopes.find((s) => s.season !== null); // scopes arrive newest-season first
  const career = scopes.find((s) => s.scopeType === "career");
  const experienceYears = player.experience ? parseInt(player.experience, 10) || 0 : 0;
  const seasonLabel =
    scope?.season != null
      ? `${scope.season}-${String((scope.season + 1) % 100).padStart(2, "0")}`
      : undefined;

  const plan = selectSections({
    scope,
    career,
    positionGroup: player.pos,
    experienceYears,
    playerId,
  });
  const has = (t: TemplateId) => plan.templates.includes(t);

  const featureTables = plan.templates.flatMap((t) =>
    resolveFeatureSections(t, sections),
  );

  // Section numbering follows render order, personnel-style.
  let n = 0;
  const code = () => String(++n).padStart(2, "0");

  return (
    <Stack spacing={0.5} sx={{ px: 2 }}>
      <HeaderBand
        name={player.name ?? playerName}
        headshot={player.headshot}
        pos={player.pos}
        heightIn={player.heightIn}
        weightLb={player.weightLb}
        ageYrs={player.ageYrs}
        seasonScope={scope}
        seasonLabel={seasonLabel}
      />

      {has("twoWaySummary") ? (
        <>
          {/* No kicker (Trevor 2026-10-30): early in a season every player is on this layout */}
          <SeasonHistory code={code()} scopes={scopes} leagueTags />
          {scope && scope.season != null && player.playerId && (
            <ShotChartSection
              code={code()}
              playerUuid={player.playerId}
              season={scope.season}
              seasonLabel={seasonLabel}
              profile={blob.shotProfile?.[String(scope.season)]}
            />
          )}
          {scope && (
            <Pair>
              <ActionProfile code={code()} scope={scope} side="off" />
              <ActionProfile code={code()} scope={scope} side="def" />
            </Pair>
          )}
        </>
      ) : (
        scope && (
          <>
            <Kicker label="Baseline" />
            {/* Team-report pages show the last 2 seasons (user call 2026-08-25);
                the payload carries 5 for other surfaces. */}
            {blob.box && blob.box.length > 0 && (
              <BoxScoreTable code={code()} box={blob.box.slice(0, 2)} />
            )}
            <AdvancedSnapshot
              code={code()}
              scope={scope}
              section={sections.find((s) => s.key === "advanced")}
            />
            <Pair>
              <ImpactHistory code={code()} scopes={scopes} />
              <WhoHeGuards scope={scope} seasonLabel={seasonLabel} />
            </Pair>
            <Pair>
              <ActionProfile code={code()} scope={scope} side="def" />
              <MatchupRoles scope={scope} seasonLabel={seasonLabel} />
            </Pair>
            <Pair>
              <ActionProfile code={code()} scope={scope} side="off" />
              <CloseoutsSection code={code()} scope={scope} />
            </Pair>
            {scope.season != null && player.playerId && (
              <ShotChartSection
                code={code()}
                playerUuid={player.playerId}
                season={scope.season}
                seasonLabel={seasonLabel}
                profile={blob.shotProfile?.[String(scope.season)]}
              />
            )}
            {has("usageOverTime") && <SeasonHistory code={code()} scopes={scopes} />}

            {featureTables.length > 0 && (
              <>
                <Kicker label="Scouting Focus" />
                <Pair>
                  {featureTables.map((section) =>
                    section.key === "offense.obs.shots" ? (
                      <ObsShotsTable key={section.key} scope={scope} section={section} />
                    ) : (
                      <BreakdownTable key={section.key} scope={scope} section={section} />
                    ),
                  )}
                </Pair>
              </>
            )}
          </>
        )
      )}
    </Stack>
  );
}
